import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { money, formatApiErrorDetail, FALLBACK_IMG, BACKEND_URL } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DollarSign, ShoppingCart, Users, Boxes, Plus, Loader2, Trash2, Pencil, Upload, ImageIcon, Store, Tag, TrendingUp, Star } from "lucide-react";
import { toast } from "sonner";
import StoreInfoTab from "@/components/admin/StoreInfoTab";
import MessagesTab from "@/components/admin/MessagesTab";
import FreeGuideTab from "@/components/admin/FreeGuideTab";
import SubscribersTab from "@/components/admin/SubscribersTab";

const CATS = ["Liquid Cultures", "Grain Spawn", "Grow Bags & Buckets", "Ebooks & Guides"];
const empty = { name: "", price: "", category: "Liquid Cultures", type: "physical", image: "", short: "", description: "", in_stock: true, stock_count: "" };

export default function Admin() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [swappingSlug, setSwappingSlug] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) navigate("/login");
  }, [ready, user, navigate]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user]);

  const save = async (e) => {
    e.preventDefault();
    const priceNum = parseFloat(form.price);
    if (isNaN(priceNum) || priceNum < 0) { toast.error("Please enter a valid price"); return; }
    setSaving(true);
    const stockCountRaw = String(form.stock_count).trim();
    const stock_count = stockCountRaw === "" ? null : Math.max(0, parseInt(stockCountRaw, 10) || 0);
    const payload = { ...form, price: priceNum, stock_count, badges: [] };
    try {
      if (editingSlug) {
        await api.put(`/products/${editingSlug}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setForm(empty); setEditingSlug(null); setDialogOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const openAdd = () => { setEditingSlug(null); setForm(empty); };
  const openEdit = (p) => {
    setEditingSlug(p.slug);
    setForm({
      name: p.name, price: p.price, category: p.category, type: p.type,
      image: p.image || "", short: p.short || "", description: p.description || "",
      in_stock: p.in_stock !== false,
      stock_count: typeof p.stock_count === "number" ? String(p.stock_count) : "",
    });
    setDialogOpen(true);
  };

  const del = async (slug) => {
    try { await api.delete(`/products/${slug}`); toast.success("Product removed"); load(); }
    catch { toast.error("Failed"); }
  };

  const toggleStock = async (slug, current) => {
    try {
      await api.patch(`/products/${slug}/stock`, { in_stock: !current });
      toast.success(!current ? "Marked in stock" : "Marked out of stock");
      load();
    } catch { toast.error("Failed"); }
  };

  const toggleFeatured = async (slug, current) => {
    try {
      await api.patch(`/products/${slug}/featured`, { featured: !current });
      toast.success(!current ? "Featured on homepage" : "Removed from homepage");
      load();
    } catch { toast.error("Failed"); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }
    setUploadingImg(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `${BACKEND_URL}${res.data.url}` }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploadingImg(false); }
  };

  const quickUpdateImage = async (product, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }
    setSwappingSlug(product.slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newImage = `${BACKEND_URL}${res.data.url}`;
      await api.put(`/products/${product.slug}`, {
        name: product.name, price: product.price, category: product.category, type: product.type,
        image: newImage, short: product.short || "", description: product.description || "",
        in_stock: product.in_stock !== false, badges: product.badges || [],
        stock_count: typeof product.stock_count === "number" ? product.stock_count : null,
      });
      toast.success("Photo updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update photo");
    } finally { setSwappingSlug(null); }
  };

  const uploadEbook = async (slug, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/products/${slug}/ebook`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Your PDF is now the download for this guide");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed (must be a PDF)");
    }
  };

  const resetEbook = async (slug) => {
    try {
      await api.delete(`/products/${slug}/ebook`);
      toast.success("Reverted to the auto-generated default PDF");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reset");
    }
  };

  if (!ready || !user || user.role !== "admin") return null;

  const cards = [
    { icon: DollarSign, label: "Revenue", value: stats ? money(stats.revenue) : "-" },
    { icon: ShoppingCart, label: "Paid Orders", value: stats?.orders ?? "-" },
    { icon: Users, label: "Customers", value: stats?.customers ?? "-" },
    { icon: Boxes, label: "Products", value: stats?.products ?? "-" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
      <p className="font-mono-label text-xs text-primary mb-3">Admin</p>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <h1 className="font-display text-4xl sm:text-5xl font-light">Dashboard</h1>
        <button onClick={() => navigate("/admin/blog")} data-testid="admin-blog-link"
          className="inline-flex items-center gap-2 font-mono-label text-[11px] border border-border hover:border-primary hover:text-primary rounded-full px-5 py-2.5 transition-colors">
          <Pencil className="w-3.5 h-3.5" /> Blog Publishing
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-12">
        {cards.map((c) => (
          <div key={c.label} className="bg-card p-6" data-testid={`stat-${c.label.toLowerCase().replace(" ", "-")}`}>
            <c.icon className="w-5 h-5 text-primary mb-3" />
            <p className="font-mono-label text-[10px] text-muted-foreground">{c.label}</p>
            <p className="font-display text-3xl mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {stats?.top_products?.length > 0 && (
        <div className="mb-12" data-testid="best-sellers">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-primary" />
            <p className="font-mono-label text-[11px] text-muted-foreground">Best sellers — what to restock &amp; promote</p>
          </div>
          <div className="border border-border divide-y divide-border">
            <div className="grid grid-cols-12 px-4 py-3 font-mono-label text-[10px] text-muted-foreground bg-card">
              <span className="col-span-5">Product</span>
              <span className="col-span-2 text-center">Units sold</span>
              <span className="col-span-2 text-center">Stock</span>
              <span className="col-span-1 text-right">Rev.</span>
              <span className="col-span-2 text-right">Promote</span>
            </div>
            {stats.top_products.map((p) => {
              const soldOut = p.in_stock === false || p.stock_count === 0;
              const low = !soldOut && typeof p.stock_count === "number" && p.stock_count > 0 && p.stock_count <= 5;
              return (
                <div key={p.slug} className="grid grid-cols-12 px-4 py-3 text-sm items-center" data-testid={`best-seller-${p.slug}`}>
                  <span className="col-span-5 truncate">{p.name}</span>
                  <span className="col-span-2 text-center font-mono">{p.units}</span>
                  <span className="col-span-2 text-center">
                    <span className={`font-mono-label text-[9px] px-2 py-1 border ${soldOut ? "text-destructive border-destructive/40" : low ? "text-amber-500 border-amber-500/40" : "text-muted-foreground border-border"}`}>
                      {soldOut ? "OUT" : typeof p.stock_count === "number" ? p.stock_count : "—"}
                    </span>
                  </span>
                  <span className="col-span-1 text-right font-mono text-xs">{money(p.revenue)}</span>
                  <span className="col-span-2 text-right">
                    <button onClick={() => toggleFeatured(p.slug, p.featured)} data-testid={`feature-btn-${p.slug}`}
                      title={p.featured ? "Featured on homepage — click to remove" : "Feature on homepage"}
                      className={`inline-flex items-center gap-1 font-mono-label text-[9px] px-2 py-1 border rounded-full transition-colors ${p.featured ? "text-primary border-primary/50 bg-primary/10" : "text-muted-foreground border-border hover:border-primary hover:text-primary"}`}>
                      <Star className={`w-3 h-3 ${p.featured ? "fill-primary" : ""}`} />
                      {p.featured ? "Featured" : "Feature"}
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {stats?.promos?.length > 0 && (
        <div className="mb-12" data-testid="promo-analytics">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-4 h-4 text-primary" />
            <p className="font-mono-label text-[11px] text-muted-foreground">Discount code usage</p>
          </div>
          <div className="border border-border divide-y divide-border">
            <div className="grid grid-cols-3 px-4 py-3 font-mono-label text-[10px] text-muted-foreground bg-card">
              <span>Code</span><span className="text-center">Orders</span><span className="text-right">Revenue</span>
            </div>
            {stats.promos.map((p) => (
              <div key={p.code} className="grid grid-cols-3 px-4 py-3 text-sm items-center" data-testid={`promo-stat-${p.code}`}>
                <span className="font-mono text-xs text-primary">{p.code}</span>
                <span className="text-center font-mono">{p.orders}</span>
                <span className="text-right font-mono">{money(p.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="tab-subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="free-guide" data-testid="tab-free-guide">Free Guide</TabsTrigger>
          <TabsTrigger value="store-info" data-testid="tab-store-info">Store Info</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          <div className="border border-border divide-y divide-border" data-testid="admin-orders">
            <div className="grid grid-cols-12 px-4 py-3 font-mono-label text-[10px] text-muted-foreground bg-card">
              <span className="col-span-3">Order</span><span className="col-span-4">Email</span>
              <span className="col-span-2">Amount</span><span className="col-span-2">Status</span><span className="col-span-1">Date</span>
            </div>
            {orders.length === 0 && <p className="px-4 py-8 text-muted-foreground text-sm">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.order_id} className="grid grid-cols-12 px-4 py-3 text-sm items-center">
                <span className="col-span-3 font-mono text-xs">#{o.order_id.slice(0, 8).toUpperCase()}</span>
                <span className="col-span-4 truncate text-muted-foreground">{o.email || "guest"}</span>
                <span className="col-span-2 font-mono">{money(o.amount)}</span>
                <span className="col-span-2">
                  <span className={`font-mono-label text-[9px] px-2 py-1 border ${o.payment_status === "paid" ? "text-primary border-primary/40" : "text-muted-foreground border-border"}`}>
                    {o.payment_status.toUpperCase()}
                  </span>
                </span>
                <span className="col-span-1 font-mono text-[10px] text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button className="rounded-full font-mono-label text-xs" data-testid="admin-add-product-btn" onClick={() => { openAdd(); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-1" /> Add Product
            </Button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editingSlug ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-4" data-testid="admin-product-form">
                  <div><Label className="font-mono-label text-[11px]">Name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="pf-name" className="mt-1" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="font-mono-label text-[11px]">Price (USD)</Label>
                      <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="pf-price" className="mt-1" /></div>
                    <div><Label className="font-mono-label text-[11px]">Type</Label>
                      <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger className="mt-1" data-testid="pf-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="physical">Physical</SelectItem>
                          <SelectItem value="digital">Digital (PDF)</SelectItem>
                        </SelectContent>
                      </Select></div>
                  </div>
                  <div><Label className="font-mono-label text-[11px]">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1" data-testid="pf-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="font-mono-label text-[11px]">Availability</Label>
                    <Select value={form.in_stock ? "in" : "out"} onValueChange={(v) => setForm({ ...form, in_stock: v === "in" })}>
                      <SelectTrigger className="mt-1" data-testid="pf-stock"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in">In Stock</SelectItem>
                        <SelectItem value="out">Out of Stock</SelectItem>
                      </SelectContent>
                    </Select></div>
                    <div><Label className="font-mono-label text-[11px]">Units left (optional)</Label>
                      <Input type="number" min="0" step="1" value={form.stock_count}
                        onChange={(e) => setForm({ ...form, stock_count: e.target.value })}
                        data-testid="pf-stock-count" placeholder="e.g. 2" className="mt-1" />
                      <p className="text-[10px] text-muted-foreground mt-1">Shows "Only N left" when 1–5. Leave blank to hide.</p>
                    </div>
                  </div>
                  <div>
                    <Label className="font-mono-label text-[11px]">Product Image</Label>
                    <div className="mt-1 flex items-start gap-3">
                      <div className="w-24 h-24 shrink-0 border border-border bg-secondary overflow-hidden flex items-center justify-center">
                        {form.image ? (
                          <img src={form.image} alt="preview" data-testid="pf-image-preview" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2">
                        <label data-testid="pf-image-upload"
                          className="inline-flex items-center gap-2 font-mono-label text-[11px] px-4 py-2.5 border border-border hover:border-primary hover:text-primary cursor-pointer transition-colors rounded-full">
                          {uploadingImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {uploadingImg ? "Uploading..." : "Upload Image"}
                          <input type="file" accept="image/*" className="hidden" disabled={uploadingImg}
                            onChange={(e) => uploadImage(e.target.files[0])} />
                        </label>
                        <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                          data-testid="pf-image" placeholder="or paste an image URL" className="text-xs" />
                        {form.image && (
                          <button type="button" onClick={() => setForm({ ...form, image: "" })}
                            data-testid="pf-image-clear" className="font-mono-label text-[9px] text-muted-foreground hover:text-destructive transition-colors">
                            REMOVE IMAGE
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div><Label className="font-mono-label text-[11px]">Short tagline</Label>
                    <Input value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} data-testid="pf-short" className="mt-1" /></div>
                  <div><Label className="font-mono-label text-[11px]">Description</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pf-description" className="mt-1" /></div>
                  <Button type="submit" disabled={saving} className="w-full rounded-full font-mono-label text-xs" data-testid="pf-submit">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingSlug ? "Save Changes" : "Create Product")}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="admin-products">
            {products.map((p) => (
              <div key={p.slug} className="bg-card p-4 flex gap-3">
                <label data-testid={`admin-swap-image-${p.slug}`}
                  className="relative w-16 h-16 shrink-0 group/img cursor-pointer border border-border overflow-hidden"
                  title="Click to change photo">
                  <img src={p.image || FALLBACK_IMG} alt={p.name} className="w-full h-full object-cover" />
                  <span className="absolute inset-0 bg-background/70 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                    {swappingSlug === p.slug
                      ? <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      : <Upload className="w-4 h-4 text-primary" />}
                  </span>
                  <input type="file" accept="image/*" className="hidden" disabled={swappingSlug === p.slug}
                    onChange={(e) => quickUpdateImage(p, e.target.files[0])} />
                </label>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg truncate">{p.name}</p>
                  <p className="font-mono text-xs text-primary">{money(p.price)}</p>
                  <p className="font-mono-label text-[9px] text-muted-foreground mt-1">{p.category}</p>
                  <button onClick={() => toggleStock(p.slug, p.in_stock !== false)} data-testid={`admin-stock-${p.slug}`}
                    className={`mt-2 font-mono-label text-[9px] px-2 py-1 border transition-colors ${p.in_stock === false ? "text-destructive border-destructive/40 hover:bg-destructive/10" : "text-primary border-primary/40 hover:bg-primary/10"}`}>
                    {p.in_stock === false ? "OUT OF STOCK" : "IN STOCK"}
                  </button>
                  <button onClick={() => toggleFeatured(p.slug, !!p.featured)} data-testid={`admin-feature-${p.slug}`}
                    title={p.featured ? "Featured on homepage" : "Feature on homepage"}
                    className={`mt-2 ml-2 inline-flex items-center gap-1 font-mono-label text-[9px] px-2 py-1 border transition-colors ${p.featured ? "text-primary border-primary/50 bg-primary/10" : "text-muted-foreground border-border hover:border-primary hover:text-primary"}`}>
                    <Star className={`w-3 h-3 ${p.featured ? "fill-primary" : ""}`} />
                    {p.featured ? "FEATURED" : "FEATURE"}
                  </button>
                  {p.type === "digital" && (
                    <>
                      <label data-testid={`admin-upload-ebook-${p.slug}`}
                        className="mt-2 ml-2 inline-block font-mono-label text-[9px] px-2 py-1 border border-border text-muted-foreground hover:text-primary hover:border-primary cursor-pointer transition-colors">
                        {p.ebook_path ? "REPLACE PDF" : "UPLOAD PDF"}
                        <input type="file" accept="application/pdf" className="hidden"
                          onChange={(e) => uploadEbook(p.slug, e.target.files[0])} />
                      </label>
                      {p.ebook_path && (
                        <button type="button" onClick={() => resetEbook(p.slug)} data-testid={`admin-reset-ebook-${p.slug}`}
                          className="mt-2 ml-2 inline-block font-mono-label text-[9px] px-2 py-1 border border-border text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors">
                          USE DEFAULT
                        </button>
                      )}
                    </>
                  )}
                </div>
                <button onClick={() => del(p.slug)} data-testid={`admin-del-${p.slug}`} className="text-muted-foreground hover:text-destructive transition-colors h-fit">
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.slug}`} className="text-muted-foreground hover:text-primary transition-colors h-fit ml-2">
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="store-info" className="mt-6">
          <StoreInfoTab />
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <MessagesTab />
        </TabsContent>

        <TabsContent value="subscribers" className="mt-6">
          <SubscribersTab />
        </TabsContent>

        <TabsContent value="free-guide" className="mt-6">
          <FreeGuideTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
